/**
 * The todo module's icon set — small, drawn SVGs in one consistent stroke, so
 * nothing on this screen is a Unicode glyph or an emoji standing in for an icon.
 * The check is the exception in shape: it is a filled-in mark that draws itself
 * on completion (see `.todo-check` in styles.css), so it carries the `check-mark`
 * class the animation keys off.
 */

const base = {
  fill: 'none' as const,
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

/** The completion mark — drawn (stroke-animated) when a row is checked. */
export function CheckIcon() {
  return (
    <svg className="todo-icon" width="17" height="17" viewBox="0 0 24 24" strokeWidth="3" {...base} aria-hidden>
      <path className="check-mark" d="M5 12.5l4.5 4.5L19 6.5" />
    </svg>
  );
}

/** The overdue marker beside a late reminder. */
export function ClockIcon() {
  return (
    <svg className="todo-icon" width="13" height="13" viewBox="0 0 24 24" strokeWidth="2" {...base} aria-hidden>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  );
}

/** The recurrence marker. */
export function RepeatIcon() {
  return (
    <svg className="todo-icon" width="13" height="13" viewBox="0 0 24 24" strokeWidth="2" {...base} aria-hidden>
      <path d="M17 2l4 4-4 4" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <path d="M7 22l-4-4 4-4" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}

/** The date attribute chip's glyph. */
export function CalendarIcon() {
  return (
    <svg className="todo-icon" width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" {...base} aria-hidden>
      <rect x="3" y="4.5" width="18" height="16" rx="2" />
      <path d="M3 9h18M8 2.5v4M16 2.5v4" />
    </svg>
  );
}

/** The assignee attribute chip's glyph. */
export function PersonIcon() {
  return (
    <svg className="todo-icon" width="14" height="14" viewBox="0 0 24 24" strokeWidth="2" {...base} aria-hidden>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
    </svg>
  );
}

/** The drag handle on a reorderable row — the universal "grab me" signifier. */
export function GripIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <circle cx="9" cy="6" r="1.6" />
      <circle cx="15" cy="6" r="1.6" />
      <circle cx="9" cy="12" r="1.6" />
      <circle cx="15" cy="12" r="1.6" />
      <circle cx="9" cy="18" r="1.6" />
      <circle cx="15" cy="18" r="1.6" />
    </svg>
  );
}

/** The up/down keyboard reorder chevrons. */
export function ChevronUpIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" strokeWidth="2.2" {...base} aria-hidden>
      <path d="M6 15l6-6 6 6" />
    </svg>
  );
}

export function ChevronDownIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" strokeWidth="2.2" {...base} aria-hidden>
      <path d="M6 9l6 6 6-6" />
    </svg>
  );
}
